import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  StyledAudioEffectProgress,
  GradientCircle,
  RingOverlay,
  MaskSvg,
  QualityLabelsOverlay,
  SvgLabelText,
  SvgLabelTitle,
  SvgLabelPercentage,
  SvgConnectorPath,
  SegmentsContainer,
  Segment,
} from './index.style';

const TOTAL_SEGMENTS = 48;
const SEGMENT_ANGLE = 360 / TOTAL_SEGMENTS;
const CIRCLE_SIZE = 200;
const MASK_PADDING = 1;
const MASK_SIZE = CIRCLE_SIZE + MASK_PADDING * 2; // keep a 1px inset for the mask
const CENTER_OFFSET = MASK_PADDING;
const MASK_CENTER = MASK_SIZE / 2;
const SEGMENT_WIDTH = 4;
const SEGMENT_X = MASK_CENTER - SEGMENT_WIDTH / 2;
const SEGMENT_INACTIVE_TOP = 12;
const SEGMENT_ACTIVE_TOP = 6;
const ACTIVE_SEGMENT_HEIGHT = 13;

const getSegmentProps = (isActive) => ({
  top: (isActive ? SEGMENT_ACTIVE_TOP : SEGMENT_INACTIVE_TOP) + CENTER_OFFSET,
  height: isActive ? ACTIVE_SEGMENT_HEIGHT : 0,
});

const AudioEffectProgress = ({ progress = 0, hasAudioStatus }) => {
  const { t } = useTranslation();

  // Calculate how many segments should be active based on progress
  const activeSegments = useMemo(
    () => Math.round((progress / 100) * TOTAL_SEGMENTS),
    [progress],
  );

  // Generate segments
  const segments = useMemo(
    () =>
      Array.from({ length: TOTAL_SEGMENTS }, (_, index) => ({
        id: index,
        rotation: index * SEGMENT_ANGLE,
        isActive: index < activeSegments,
      })),
    [activeSegments],
  );

  // Quality labels configuration based on Figma design
  const qualityLabels = [
    {
      id: 'excellent',
      title: t('excellent'),
      percentage: '> 80%',
      textAnchor: 'middle',
      textPosition: { x: -105, y: 64 },
      connectorPath: {
        d: `M0.000325441 2.66675C0.000325441 1.19399 1.19423 8.2016e-05 2.66699 8.2016e-05C4.13975 8.2016e-05 5.33366 1.19399 5.33366 2.66675C5.33366 4.13951 4.13975 5.33341 2.66699 5.33341C1.19423 5.33341 0.000325441 4.13951 0.000325441 2.66675ZM69.991 30.7859C70.2013 30.9649 70.2268 31.2804 70.0478 31.4907C69.8689 31.7011 69.5533 31.7265 69.343 31.5476L69.667 31.1667L69.991 30.7859ZM38.4082 4.57348L38.7322 4.19265L38.4082 4.57348ZM2.66699 2.66675V2.16675H33.2244V2.66675V3.16675H2.66699V2.66675ZM38.4082 4.57348L38.7322 4.19265L69.991 30.7859L69.667 31.1667L69.343 31.5476L38.0842 4.95431L38.4082 4.57348ZM33.2244 2.66675V2.16675C35.2426 2.16675 37.195 2.88488 38.7322 4.19265L38.4082 4.57348L38.0842 4.95431C36.7279 3.80039 35.0052 3.16675 33.2244 3.16675V2.66675Z`,
        transform: 'translate(-70, 70)',
        style: {
          fill: 'rgba(28, 28, 30, 0.24)',
          stroke: 'none',
        },
      },
    },
    {
      id: 'good',
      title: t('good'),
      percentage: '60%',
      textAnchor: 'middle',
      textPosition: { x: -57, y: 238 },
      connectorPath: {
        d: `M0.000325441 29C0.000325441 30.4728 1.19423 31.6667 2.66699 31.6667C4.13975 31.6667 5.33366 30.4728 5.33366 29C5.33366 27.5272 4.13975 26.3333 2.66699 26.3333C1.19423 26.3333 0.000325441 27.5272 0.000325441 29ZM69.991 12.8808C70.2013 12.7019 70.2268 12.3863 70.0478 12.176C69.8689 11.9657 69.5533 11.9402 69.343 12.1192L69.667 12.5L69.991 12.8808ZM38.4082 27.0933L38.7322 27.4741L38.4082 27.0933ZM2.66699 29V29.5H33.2244V29V28.5H2.66699V29ZM38.4082 27.0933L38.7322 27.4741L69.991 12.8808L69.667 12.5L69.343 12.1192L38.0842 26.7124L38.4082 27.0933ZM33.2244 29V29.5C35.2426 29.5 37.195 28.7819 38.7322 27.4741L38.4082 27.0933L38.0842 26.7124C36.7279 27.8664 35.0052 28.5 33.2244 28.5V29Z`,
        transform: 'translate(-27.33, 214.5)',
        style: {
          fill: 'rgba(28, 28, 30, 0.24)',
          stroke: 'none',
        },
      },
    },
    {
      id: 'pass',
      title: t('pass'),
      percentage: '30%',
      textAnchor: 'start',
      textPosition: { x: 335, y: 98 },
      connectorPath: {
        d: `M70.1667 2.66675C70.1667 1.19399 68.9728 8.2016e-05 67.5 8.2016e-05C66.0272 8.2016e-05 64.8333 1.19399 64.8333 2.66675C64.8333 4.13951 66.0272 5.33341 67.5 5.33341C68.9728 5.33341 70.1667 4.13951 70.1667 2.66675ZM0.17601 30.7859C-0.034317 30.9649 -0.059761 31.2804 0.119171 31.4907C0.298103 31.7011 0.613663 31.7265 0.82399 31.5476L0.5 31.1667L0.17601 30.7859ZM31.7588 4.57348L31.4348 4.19265L31.7588 4.57348ZM67.5 2.66675V2.16675H36.9426V2.66675V3.16675H67.5V2.66675ZM31.7588 4.57348L31.4348 4.19265L0.17601 30.7859L0.5 31.1667L0.82399 31.5476L32.0827 4.95431L31.7588 4.57348ZM36.9426 2.66675V2.16675C34.9244 2.16675 32.972 2.88488 31.4348 4.19265L31.7588 4.57348L32.0827 4.95431C33.4391 3.80039 35.1618 3.16675 36.9426 3.16675V2.66675Z`,
        transform: 'translate(250, 98)',
        style: {
          fill: 'rgba(28, 28, 30, 0.24)',
          stroke: 'none',
        },
      },
    },
  ];

  // Generate mask ID for segments
  const maskId = useMemo(() => `segment-mask-${progress}`, [progress]);
  const renderSegments = () => (
    <MaskSvg>
      <defs>
        <mask id={maskId} maskUnits="userSpaceOnUse">
          <rect width={MASK_SIZE} height={MASK_SIZE} fill="white" />
          {segments.map((segment) => {
            const { top, height } = getSegmentProps(segment.isActive);
            return (
              <rect
                key={segment.id}
                x={SEGMENT_X}
                y={top}
                width={SEGMENT_WIDTH}
                height={height}
                rx="2"
                ry="2"
                fill="black"
                transform={`rotate(${segment.rotation} ${MASK_CENTER} ${MASK_CENTER})`}
              />
            );
          })}
        </mask>
      </defs>
    </MaskSvg>
  );

  return (
    <StyledAudioEffectProgress>
      <GradientCircle />
      {hasAudioStatus && (
        <SegmentsContainer>
          {segments.map((segment) => (
            <Segment
              key={segment.id}
              rotation={segment.rotation}
              isActive={segment.isActive}
            />
          ))}
        </SegmentsContainer>
      )}
      {hasAudioStatus && renderSegments()}
      <RingOverlay maskId={hasAudioStatus ? maskId : undefined} />
      {hasAudioStatus && (
        <QualityLabelsOverlay
          width="248"
          height="248"
          viewBox="0 0 248 248"
          preserveAspectRatio="xMidYMid meet"
        >
          {qualityLabels.map(
            ({
              id,
              connectorPath,
              textAnchor,
              textPosition,
              title,
              percentage,
            }) => (
              <g key={id}>
                <SvgConnectorPath
                  d={connectorPath.d}
                  transform={connectorPath.transform}
                  style={connectorPath.style}
                />
                <SvgLabelText textAnchor={textAnchor}>
                  <SvgLabelTitle x={textPosition.x} y={textPosition.y}>
                    {title}
                  </SvgLabelTitle>
                  <SvgLabelPercentage
                    x={textPosition.x}
                    y={textPosition.y + 18}
                  >
                    {percentage}
                  </SvgLabelPercentage>
                </SvgLabelText>
              </g>
            ),
          )}
        </QualityLabelsOverlay>
      )}
    </StyledAudioEffectProgress>
  );
};

export default AudioEffectProgress;
